import { useState } from "react";
import { StockItem, AlternativeItem, LinkedItem } from "@/types/stock-item";
import { Button } from "@/components/ui/button";
import { Plus, X, Search } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";

interface StockItemRelationshipsTabProps {
  item: StockItem;
  isEditMode: boolean;
  onItemChange: (item: StockItem) => void;
  allStockItems?: StockItem[]; // For searching/selecting items
}

export function StockItemRelationshipsTab({
  item,
  isEditMode,
  onItemChange,
  allStockItems = [],
}: StockItemRelationshipsTabProps) {
  const [isAddAlternativeDialogOpen, setIsAddAlternativeDialogOpen] = useState(false);
  const [isAddLinkedDialogOpen, setIsAddLinkedDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedRelationshipType, setSelectedRelationshipType] = useState<LinkedItem["relationshipType"]>("Accessory");

  const alternativeItems = item.alternativeItems || [];
  const linkedItems = item.linkedItems || [];

  // Filter stock items based on search query (exclude current item)
  const filteredStockItems = allStockItems.filter(
    (stockItem) =>
      stockItem.code !== item.code &&
      (stockItem.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
        stockItem.description.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const handleAddAlternativeItem = (stockItem: StockItem) => {
    const newAlternative: AlternativeItem = {
      id: `alt-${Date.now()}-${Math.random()}`,
      stockCode: stockItem.code,
      description: stockItem.description,
      unitPrice: stockItem.unitPrice,
      physicalQuantity: stockItem.physicalQuantity,
      category: stockItem.category,
    };

    const updatedItem = {
      ...item,
      alternativeItems: [...alternativeItems, newAlternative],
    };

    onItemChange(updatedItem);
    setIsAddAlternativeDialogOpen(false);
    setSearchQuery("");
  };

  const handleRemoveAlternativeItem = (id: string) => {
    const updatedItem = {
      ...item,
      alternativeItems: alternativeItems.filter((alt) => alt.id !== id),
    };
    onItemChange(updatedItem);
  };

  const handleAddLinkedItem = (stockItem: StockItem) => {
    const newLinkedItem: LinkedItem = {
      id: `link-${Date.now()}-${Math.random()}`,
      stockCode: stockItem.code,
      description: stockItem.description,
      relationshipType: selectedRelationshipType,
      physicalQuantity: stockItem.physicalQuantity,
      unitPrice: stockItem.unitPrice,
      category: stockItem.category,
    };

    const updatedItem = {
      ...item,
      linkedItems: [...linkedItems, newLinkedItem],
    };

    onItemChange(updatedItem);
    setIsAddLinkedDialogOpen(false);
    setSearchQuery("");
    setSelectedRelationshipType("Accessory");
  };

  const handleRemoveLinkedItem = (id: string) => {
    const updatedItem = {
      ...item,
      linkedItems: linkedItems.filter((link) => link.id !== id),
    };
    onItemChange(updatedItem);
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
    }).format(value);
  };

  const getRelationshipTypeBadgeVariant = (type: LinkedItem["relationshipType"]) => {
    switch (type) {
      case "Accessory":
        return "default";
      case "Spare Part":
        return "secondary";
      case "Related Product":
        return "outline";
      case "Complementary":
        return "default";
      case "Replacement":
        return "secondary";
      default:
        return "outline";
    }
  };

  return (
    <div className="space-y-8">
      {/* Alternative Items Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold">Alternative Items</h3>
            <p className="text-sm text-muted-foreground">
              Items that can be used as substitutes when this item is out of stock
            </p>
          </div>
          {isEditMode && (
            <Button
              onClick={() => setIsAddAlternativeDialogOpen(true)}
              size="sm"
              variant="outline"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Alternative
            </Button>
          )}
        </div>

        {alternativeItems.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground border rounded-lg border-dashed">
            <p>No alternative items defined</p>
            {isEditMode && (
              <p className="text-sm mt-1">Click "Add Alternative" to add substitute items</p>
            )}
          </div>
        ) : (
          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Stock Code</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-right">Quantity</TableHead>
                  {isEditMode && <TableHead className="w-[50px]"></TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {alternativeItems.map((alt) => (
                  <TableRow key={alt.id}>
                    <TableCell className="font-medium">{alt.stockCode}</TableCell>
                    <TableCell>{alt.description}</TableCell>
                    <TableCell className="text-right">
                      <span
                        className={
                          alt.physicalQuantity > 0
                            ? "text-green-600 dark:text-green-400"
                            : "text-red-600 dark:text-red-400"
                        }
                      >
                        {alt.physicalQuantity}
                      </span>
                    </TableCell>
                    {isEditMode && (
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveAlternativeItem(alt.id!)}
                          className="h-8 w-8 p-0"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* Linked Items Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold">Linked Items</h3>
            <p className="text-sm text-muted-foreground">
              Related items such as accessories, spare parts, or complementary products
            </p>
          </div>
          {isEditMode && (
            <Button
              onClick={() => setIsAddLinkedDialogOpen(true)}
              size="sm"
              variant="outline"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Linked Item
            </Button>
          )}
        </div>

        {linkedItems.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground border rounded-lg border-dashed">
            <p>No linked items defined</p>
            {isEditMode && (
              <p className="text-sm mt-1">Click "Add Linked Item" to add related products</p>
            )}
          </div>
        ) : (
          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Stock Code</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-right">Quantity</TableHead>
                  {isEditMode && <TableHead className="w-[50px]"></TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {linkedItems.map((link) => (
                  <TableRow key={link.id}>
                    <TableCell className="font-medium">{link.stockCode}</TableCell>
                    <TableCell>{link.description}</TableCell>
                    <TableCell className="text-right">
                      <span
                        className={
                          link.physicalQuantity > 0
                            ? "text-green-600 dark:text-green-400"
                            : "text-red-600 dark:text-red-400"
                        }
                      >
                        {link.physicalQuantity}
                      </span>
                    </TableCell>
                    {isEditMode && (
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveLinkedItem(link.id!)}
                          className="h-8 w-8 p-0"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* Add Alternative Item Dialog */}
      <Dialog open={isAddAlternativeDialogOpen} onOpenChange={setIsAddAlternativeDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Alternative Item</DialogTitle>
            <DialogDescription>
              Search and select an item that can be used as a substitute for {item.code}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="search-alternative">Search Stock Items</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="search-alternative"
                  placeholder="Search by code or description..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            {searchQuery && (
              <div className="border rounded-lg max-h-[400px] overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Code</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead className="text-right">Price</TableHead>
                      <TableHead className="text-right">Stock</TableHead>
                      <TableHead className="w-[100px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredStockItems.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                          No items found
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredStockItems.slice(0, 20).map((stockItem) => {
                        const isAlreadyAdded = alternativeItems.some(
                          (alt) => alt.stockCode === stockItem.code
                        );
                        return (
                          <TableRow key={stockItem.code}>
                            <TableCell className="font-medium">{stockItem.code}</TableCell>
                            <TableCell>{stockItem.description}</TableCell>
                            <TableCell>{stockItem.category}</TableCell>
                            <TableCell className="text-right">
                              {formatCurrency(stockItem.unitPrice)}
                            </TableCell>
                            <TableCell className="text-right">{stockItem.physicalQuantity}</TableCell>
                            <TableCell>
                              <Button
                                size="sm"
                                onClick={() => handleAddAlternativeItem(stockItem)}
                                disabled={isAlreadyAdded}
                              >
                                {isAlreadyAdded ? "Added" : "Add"}
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddAlternativeDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Linked Item Dialog */}
      <Dialog open={isAddLinkedDialogOpen} onOpenChange={setIsAddLinkedDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Linked Item</DialogTitle>
            <DialogDescription>
              Search and select a related item to link with {item.code}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="relationship-type">Relationship Type</Label>
              <Select
                value={selectedRelationshipType}
                onValueChange={(value) => setSelectedRelationshipType(value as LinkedItem["relationshipType"])}
              >
                <SelectTrigger id="relationship-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Accessory">Accessory</SelectItem>
                  <SelectItem value="Spare Part">Spare Part</SelectItem>
                  <SelectItem value="Related Product">Related Product</SelectItem>
                  <SelectItem value="Complementary">Complementary</SelectItem>
                  <SelectItem value="Replacement">Replacement</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="search-linked">Search Stock Items</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="search-linked"
                  placeholder="Search by code or description..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            {searchQuery && (
              <div className="border rounded-lg max-h-[400px] overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Code</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead className="text-right">Price</TableHead>
                      <TableHead className="text-right">Stock</TableHead>
                      <TableHead className="w-[100px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredStockItems.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                          No items found
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredStockItems.slice(0, 20).map((stockItem) => {
                        const isAlreadyAdded = linkedItems.some(
                          (link) => link.stockCode === stockItem.code
                        );
                        return (
                          <TableRow key={stockItem.code}>
                            <TableCell className="font-medium">{stockItem.code}</TableCell>
                            <TableCell>{stockItem.description}</TableCell>
                            <TableCell>{stockItem.category}</TableCell>
                            <TableCell className="text-right">
                              {formatCurrency(stockItem.unitPrice)}
                            </TableCell>
                            <TableCell className="text-right">{stockItem.physicalQuantity}</TableCell>
                            <TableCell>
                              <Button
                                size="sm"
                                onClick={() => handleAddLinkedItem(stockItem)}
                                disabled={isAlreadyAdded}
                              >
                                {isAlreadyAdded ? "Added" : "Add"}
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddLinkedDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
